import jsQR from 'jsqr'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MANUAL_RESERVATION_CODE_MAX_LEN } from './dialogScanConstants'

const clampManualInput = (value: string) =>
  value.slice(0, MANUAL_RESERVATION_CODE_MAX_LEN)

type QrScannerPanelProps = {
  active: boolean
  scanPaused?: boolean
  loading?: boolean
  /** Raw token from QR decode, or manual code (max 10 chars), sent to lookup API as-is (trimmed). */
  onDecodedPayload: (token: string) => void
}
export function QrScannerPanel({
  active,
  scanPaused = false,
  loading = false,
  onDecodedPayload,
}: QrScannerPanelProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number>(0)
  const decodeHandlerRef = useRef(onDecodedPayload)
  decodeHandlerRef.current = onDecodedPayload

  const [manualCode, setManualCode] = useState('')
  const [cameraBlocked, setCameraBlocked] = useState(false)

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  useEffect(() => {
    if (!active || scanPaused) {
      stopCamera()
      return
    }
    setCameraBlocked(false)
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraBlocked(true)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop())
          return
        }
        streamRef.current = stream
        const v = videoRef.current
        if (v) {
          v.srcObject = stream
          await v.play()
        }
      } catch {
        if (!cancelled) setCameraBlocked(true)
      }
    })()
    return () => {
      cancelled = true
      stopCamera()
    }
  }, [active, scanPaused, stopCamera])

  useEffect(() => {
    if (!active || scanPaused || cameraBlocked) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      return
    }
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) return

    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    const tick = () => {
      if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        const w = video.videoWidth
        const h = video.videoHeight
        if (w && h) {
          canvas.width = w
          canvas.height = h
          ctx.drawImage(video, 0, 0, w, h)
          const imageData = ctx.getImageData(0, 0, w, h)
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert',
          })
          if (code?.data) {
            const token = code.data.trim()
            if (token) {
              decodeHandlerRef.current(token)
              return
            }
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [active, scanPaused, cameraBlocked])

  const submitManual = () => {
    const token = manualCode.trim()
    if (!token) return
    onDecodedPayload(token)
  }

  return (
    <div className="scan-qr-scanner">
      <div className="scan-qr-scanner__viewport border-round-xl overflow-hidden">
        {cameraBlocked ? (
          <div
            className="scan-qr-scanner__camera-error flex flex-column align-items-center justify-content-center text-center px-4 h-full w-full"
            role="alert"
          >
            <div className="flex align-items-center gap-2 justify-content-center flex-wrap mb-2">
              <div
                className="w-2rem h-2rem scan-qr-heading-icon scan-qr-heading-icon--camera-error flex align-items-center justify-content-center border-circle"
                aria-hidden
              >
                <i className="isax-video-slash text-white scan-qr-heading-icon__glyph" />
              </div>
              <p className="m-0 font-semibold text-base line-height-3 text-white">
                {t('Camera access is not available')}
              </p>
            </div>
            <p className="m-0 text-sm line-height-3 opacity-90 scan-qr-scanner__camera-error-detail">
              {t(
                'Allow camera permission for this site in your browser settings to scan QR codes. You can also enter the reservation code below.'
              )}
            </p>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="scan-qr-scanner__video"
              playsInline
              muted
              autoPlay
            />
            <div className="scan-qr-scanner__overlay">
              <div className="scan-qr-scanner__frame">
                <span className="scan-qr-scanner__corner scan-qr-scanner__corner--tl" />
                <span className="scan-qr-scanner__corner scan-qr-scanner__corner--tr" />
                <span className="scan-qr-scanner__corner scan-qr-scanner__corner--bl" />
                <span className="scan-qr-scanner__corner scan-qr-scanner__corner--br" />
                <div className="scan-qr-scanner__laser" />
              </div>
            </div>
          </>
        )}
      </div>
      <canvas ref={canvasRef} className="scan-qr-scanner__canvas" aria-hidden />

      <p className="scan-qr-scanner__hint text-center m-0 mt-3 text-sm">
        {cameraBlocked
          ? t(
              'Enter the reservation code below, or allow camera and reopen this screen.'
            )
          : t('Align the QR code within the frame')}
      </p>

      <div className="scan-qr-scanner__manual surface-ground border-round-xl p-3 mt-4">
        <p className="m-0 mb-2 text-sm text-gray-900">
          {t('Or enter the code manually.')}
        </p>
        <div className="scan-qr-manual-row">
          <div className="scan-qr-manual-row__field">
            <InputText
              value={manualCode}
              onChange={(e) => setManualCode(clampManualInput(e.target.value))}
              placeholder={t('Enter code')}
              className="w-full"
              maxLength={MANUAL_RESERVATION_CODE_MAX_LEN}
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              onKeyDown={(e) => e.key === 'Enter' && !loading && submitManual()}
            />
          </div>
          <Button
            type="button"
            label={t('Confirm')}
            className="scan-qr-manual-row__confirm scan-qr-accent-btn"
            loading={loading}
            disabled={loading || !manualCode.trim()}
            onClick={submitManual}
          />
        </div>
      </div>
    </div>
  )
}
